import { AsyncPipe, NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { ContentfulService } from '@core/services/contentful.service';
import {
    KnowledgeArticle,
    KnowledgeCategory,
    KnowledgeDestination,
    KnowledgeDestinationAllTopicsDescription,
} from '@generated/graphql';
import { Observable, ReplaySubject } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { RichTextDocumentComponent } from '../../contentful-dynamic/rich-text/rich-text-document/rich-text-document.component';
import { KnowledgeCategoryListItemComponent } from '../knowledge-category-list-item/knowledge-category-list-item.component';

export type CategoryArticles = {
    category: KnowledgeCategory;
    articles: KnowledgeArticle[];
};

@Component({
    selector: 'app-knowledge-category-list',
    templateUrl: './knowledge-category-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RichTextDocumentComponent,
        NgClass,
        KnowledgeCategoryListItemComponent,
        AsyncPipe,
    ],
})
export class KnowledgeCategoryListComponent implements OnInit, OnDestroy {
    @Input()
    set destination(destination: KnowledgeDestination) {
        this._destination.next(destination);
    }
    @Input() isSidebar: boolean = false;
    @Input() allTopicsDescription: KnowledgeDestinationAllTopicsDescription;
    articles: Observable<KnowledgeArticle[]>;
    categories: Observable<CategoryArticles[]>;
    isEmpty: Observable<boolean>;
    private _destination = new ReplaySubject<KnowledgeDestination>(1);

    constructor(private contentfulService: ContentfulService) {}

    ngOnInit(): void {
        this.articles = this._destination.pipe(
            switchMap(destination =>
                this.contentfulService.articlesByDestination(
                    destination?.sys.id
                )
            ),
            map(
                articlesCollection =>
                    articlesCollection?.data.knowledgeDestination.linkedFrom
                        .knowledgeArticleCollection.items || []
            )
        );
        this.categories = this.articles.pipe(
            map(articles => this.organizeArticlesByCategory(articles)),
            map(articles =>
                [...articles.values()].sort(
                    (a, b) => a.articles.length - b.articles.length
                )
            ),
            shareReplay(1)
        );
    }

    organizeArticlesByCategory(
        articles: KnowledgeArticle[]
    ): Map<string, CategoryArticles> {
        const categories = new Map<string, CategoryArticles>();
        const filteredArticles = articles.filter(
            article =>
                (!this.isSidebar ||
                    (this.isSidebar && article?.showInSidebar)) &&
                article?.category?.sys?.id
        );
        for (let article of filteredArticles) {
            if (categories.has(article.category.sys.id)) {
                categories.get(article.category.sys.id).articles.push(article);
            } else {
                categories.set(article.category.sys.id, {
                    category: article.category,
                    articles: [article],
                });
            }
        }
        return categories;
    }

    ngOnDestroy(): void {
        this._destination.complete();
    }
}
